import { ref, onMounted, shallowRef } from 'vue';
import { Loader } from '@googlemaps/js-api-loader';
import {
    MAP_ID,
    MAP_CENTER,
    MAP_INITIAL_ZOOM,
    MAP_INITIAL_TILT,
    MAP_MIN_ZOOM,
    MAP_MAX_ZOOM,
    MAP_BOUNDS
} from '../../constants';

// Get Google Maps API key from environment variables
const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;

if (!apiKey) {
    throw new Error('VITE_GOOGLE_MAPS_API_KEY is not defined. Please create a .env file with your API key.');
}

// Map configuration
const mapOptions = ref({
    center: MAP_CENTER,
    zoom: MAP_INITIAL_ZOOM,
    tilt: MAP_INITIAL_TILT,
    gestureHandling: 'greedy',
    mapTypeControl: false,
});

// Note: Map styles should be configured in Google Cloud Console when using mapId
// Styles are not set here because MAP_ID is present
// If you need custom styles, configure them via:
// https://console.cloud.google.com/google/maps-apis/style/map-styles

// Create a function to initialize the map
export const initializeMap = () => {
    const map = shallowRef(null);
    const mapError = ref(null);

    onMounted(async () => {
        try {
            const mapElement = document.getElementById('map');
            if (!mapElement) {
                throw new Error('Map element not found in DOM');
            }

            const loader = new Loader({ apiKey: apiKey });
            await loader.importLibrary('core');

            // Create a new LatLngBounds object
            const bounds = new google.maps.LatLngBounds();

            // Define southwest and northeast corners using constants
            const southwest = new google.maps.LatLng(
                MAP_BOUNDS.southwest.lat,
                MAP_BOUNDS.southwest.lng
            );
            const northeast = new google.maps.LatLng(
                MAP_BOUNDS.northeast.lat,
                MAP_BOUNDS.northeast.lng
            );

            // Extend the bounds to include both corners
            bounds.extend(southwest);
            bounds.extend(northeast);

            // When mapId is present, styles must be controlled via Google Cloud Console
            // Setting styles property with mapId causes a warning
            map.value = new google.maps.Map(mapElement, {
                center: mapOptions.value.center,
                mapId: MAP_ID,
                zoom: mapOptions.value.zoom,
                tilt: mapOptions.value.tilt,
                gestureHandling: mapOptions.value.gestureHandling,
                rotateControl: true,
                streetViewControl: false,
                mapTypeControl: mapOptions.value.mapTypeControl,
                minZoom: MAP_MIN_ZOOM,
                maxZoom: MAP_MAX_ZOOM,
                zoomControl: false,
                disableDefaultUI: true,
                isFractionalZoomEnabled: true,
                fullscreenControl: false,
            });
        } catch (error) {
            console.error('Failed to initialize Google Maps:', error);
            mapError.value = error.message || 'Failed to load map. Please refresh the page.';
            // You could emit an event or show a user-friendly error message here
        }
    });

    return { map, mapError };
};
